import { createContext, useContext, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";

type Role = "user" | "admin" | "superadmin";

interface RoleState {
  role: Role;
  loading: boolean;
  // Strict role checks
  isUser: boolean;
  isAdmin: boolean;        // strictly 'admin' only
  isSuperAdmin: boolean;
  // Inclusive access checks (use these for route guards)
  hasAdminAccess: boolean;      // admin OR superadmin
  hasSuperAdminAccess: boolean; // superadmin only
  // Permission helpers
  canInviteRole: (targetRole: Role) => boolean;
  canRemoveRole: (targetRole: Role) => boolean;
  canRemoveRoles: boolean; // kept for backwards compatibility
  canAccessInfra: boolean;
}

const RoleContext = createContext<RoleState>({
  role: "user",
  loading: true,
  isUser: true,
  isAdmin: false,
  isSuperAdmin: false,
  hasAdminAccess: false,
  hasSuperAdminAccess: false,
  canInviteRole: () => false,
  canRemoveRole: () => false,
  canRemoveRoles: false,
  canAccessInfra: false,
});

export function RoleProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();

  const { data: roleData, isLoading } = useQuery({
    queryKey: ["user-role", user?.id],
    queryFn: async () => {
      if (!user?.id) return "user" as Role;
      const { data, error } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id)
        .single();
      if (error) return "user" as Role;
      return (data?.role ?? "user") as Role;
    },
    enabled: !!user?.id,
    // A-08: reduced from 2 min to 30 s — limits window where a revoked role stays valid
    staleTime: 30 * 1000,
    // Re-verify role when the user switches back to this tab
    refetchOnWindowFocus: true,
  });

  const role: Role = user ? (roleData ?? "user") : "user";
  const isSuperAdmin = role === "superadmin";
  const isAdmin = role === "admin";
  const hasAdminAccess = isAdmin || isSuperAdmin;
  const hasSuperAdminAccess = isSuperAdmin;

  const canInviteRole = (targetRole: Role): boolean => {
    if (isSuperAdmin) return true;
    if (isAdmin && targetRole === "admin") return true;
    return false;
  };

  const canRemoveRole = (targetRole: Role): boolean => {
    if (isSuperAdmin) return true;
    if (isAdmin && targetRole === "admin") return true;
    return false;
  };

  return (
    <RoleContext.Provider
      value={{
        role,
        loading: isLoading && !!user,
        isUser: role === "user",
        isAdmin,
        isSuperAdmin,
        hasAdminAccess,
        hasSuperAdminAccess,
        canInviteRole,
        canRemoveRole,
        canRemoveRoles: isSuperAdmin,
        canAccessInfra: isSuperAdmin,
      }}
    >
      {children}
    </RoleContext.Provider>
  );
}

export const useRole = () => useContext(RoleContext);
